import { useEffect, useState } from 'react';
import { useKeycloak } from '../../authentication/keycloak/KeycloakProvider';
import {
  getWorkplaceQrCode,
  type WorkplaceQrCode,
  type WorkplaceQrCodeFailure,
} from '../services/floorplan-editor.service';

export interface WorkplaceQrCodeState {
  readonly loading: boolean;
  readonly data?: WorkplaceQrCode;
  readonly error?: WorkplaceQrCodeFailure;
}

/**
 * The QR sticker on the selected desk.
 *
 * Fetched per selected desk rather than with the plan: a floorplan carries hundreds of desks
 * and the inspector shows one at a time, so asking for all of them up front would be a large
 * request to answer a question about a single seat. One small request per desk selection is
 * the cheaper side of that trade.
 */
export const useWorkplaceQrCode = (workplaceId: string | undefined): WorkplaceQrCodeState => {
  const keycloak = useKeycloak();
  const [data, setData] = useState<WorkplaceQrCode | undefined>();
  const [error, setError] = useState<WorkplaceQrCodeFailure | undefined>();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!workplaceId) {
      setData(undefined);
      setError(undefined);
      setLoading(false);
      return undefined;
    }

    let active = true;
    setLoading(true);
    setError(undefined);
    // Cleared up front so the previous desk's code cannot be read as this one's while the
    // request is in flight — a QR code shown against the wrong desk is worse than none.
    setData(undefined);

    getWorkplaceQrCode(workplaceId, keycloak)
      .then((result) => {
        if (!active) return;

        if (result.status !== 'ok') {
          setError(result.status);
          return;
        }

        setData(result.data);
      })
      .catch(() => {
        if (active) setError('failed');
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [workplaceId, keycloak]);

  return { loading, data, error };
};

export default useWorkplaceQrCode;
